
import { employeeApi, shiftApi, shiftSwapApi } from '@/api';
import Button from '@/components/buttons/Button.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import EmployeeAvatar from '@/components/employee/EmployeeAvatar.vue';
import BubbleIcon from '@/components/interface/BubbleIcon.vue';
import { useUserCan } from '@/lib/permission/userCanComposable';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import store from '@/store';
import { formatCurrency } from '@/util/currencyFunctions';
import ShiftSummary from '@/views/schedule/components/ShiftSummary.vue';
import ShiftDialogHeader from '@/views/schedule/dialogs/components/ShiftDialogHeader.vue';
import { PropType } from 'vue';
import {
  Employee,
  Shift,
  ShiftSwap,
  ShiftSwapStatusEnum,
} from '../../../api/v1';
import InputGroup from '../../components/form/InputGroup.vue';
import LoadingOverlay from '../../components/loaders/LoadingOverlay.vue';
import {
  fullName,
  isZeroHourEmployee,
} from '../../lib/employee/employeeFunctions';
import { Icon } from '../../lib/enum/Icon';
import { belongsToEmployee } from '../../lib/filters/employeeFilters';
import { Entity } from '../../lib/realtime/RealtimeEntity';
import {
  presentTotalDuration,
  totalDurationOfShiftsInHours,
} from '../../lib/schedule-v2/shiftV2Functions';
import { showSuccess } from '../../plugins/ToastNotifications';
import {
  addMinutes,
  startOfNextWeek,
  startOfWeek,
} from '../../util/dateArithmetic';
import { getEntity } from '../../util/entityFunctions';
import { viewport } from '../../util/windowFunctions';

export default {
  name: 'ShiftSwapReviewDialog',

  components: {
    LoadingOverlay,
    InputGroup,
    ShiftSummary,
    Button,
    BubbleIcon,
    EmployeeAvatar,
    Dialog,
    ShiftDialogHeader,
  },

  props: {
    shiftSwap: {
      type: Object as PropType<ShiftSwap>,
      required: true,
    },
  },

  data() {
    return {
      submitting: false,
      ShiftSwapStatusEnum,
      viewport,
      Icon,
      userCanWithScope: useUserCan(store.getters.loggedInEmployee),
      requesterShifts: realtimeQuery(Entity.Shift).empty(),
      recipientShifts: realtimeQuery(Entity.Shift).empty(),
      swapShifts: realtimeQuery(Entity.Shift)
        .whereIn('id', [
          this.shiftSwap.shiftId,
          this.shiftSwap.replacementShiftId,
        ])
        .fetch(),
      shiftCosts: [],
      employees: realtimeQuery(Entity.Employee).fetch(),
      presentTotalDuration,
    };
  },

  computed: {
    timezone: () => store.getters.timezone,
    startOfWeek: () => store.state.settings.companySettings.startOfWeek,
    currencyCode: (): string =>
      store.state.settings.companySettings.currencyCode,
    open: {
      get() {
        return !!this.shiftSwap;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    requester(): Employee {
      return getEntity(this.shiftSwap.employeeId, this.employees.data);
    },
    requesterHoursPerWeek(): number {
      return (
        this.employees.data.find(belongsToEmployee(this.shiftSwap.employeeId))
          ?.workingHoursPerWeek ?? 0
      );
    },
    recipientHoursPerWeek(): number {
      return (
        this.employees.data.find(belongsToEmployee(this.shiftSwap.recipientId))
          ?.workingHoursPerWeek ?? 0
      );
    },
    recipient(): Employee {
      return getEntity(this.shiftSwap.recipientId, this.employees.data);
    },
    requesterScheduledHours(): number {
      return totalDurationOfShiftsInHours(this.requesterShifts);
    },
    recipientScheduledHours(): number {
      return totalDurationOfShiftsInHours(this.recipientShifts);
    },
    requesterProposedHours(): number {
      const shifts = [
        ...this.requesterShifts.filter((s) => s.id !== this.shiftSwap.shiftId),
        this.replacementShift,
      ];
      return totalDurationOfShiftsInHours(shifts);
    },
    recipientProposedHours(): number {
      const shifts = [
        ...this.recipientShifts.filter(
          (s) => s.id !== this.shiftSwap.replacementShiftId,
        ),
        this.unwantedShift,
      ];
      return totalDurationOfShiftsInHours(shifts);
    },
    unwantedShift(): Shift {
      return getEntity(this.shiftSwap.shiftId, this.swapShifts.data);
    },
    replacementShift(): Shift {
      return getEntity(this.shiftSwap.replacementShiftId, this.swapShifts.data);
    },
    loading(): boolean {
      return (
        this.swapShifts.isLoading ||
        this.employees.isLoading ||
        this.requesterShifts.isLoading ||
        this.recipientShifts.isLoading ||
        this.submitting
      );
    },
    currentCost(): number {
      return this.unwantedShift.cost + this.replacementShift.cost;
    },
    expectedCost(): number {
      return (
        this.getShiftCost(this.recipient) + this.getShiftCost(this.requester)
      );
    },
    costDifference(): string {
      const diff = this.expectedCost - this.currentCost;
      return formatCurrency(diff, this.currencyCode);
    },
  },

  watch: {
    'swapShifts.data': {
      handler(shifts) {
        // get shift costs
        if (
          !this.requester ||
          !this.recipient ||
          !shifts.length ||
          !this.userCanWithScope.viewLabourCosts()
        ) {
          this.shiftCosts = [];
          return;
        }
        employeeApi
          .bulkCalculateWorkingTimeCost({
            bulkCalculateWorkingTimeCostData: {
              bulkCalculateWorkingTimeCostData: [
                {
                  employeeId: this.requester.id,
                  jobRoleId: this.replacementShift.jobRoleId,
                  startTime: this.replacementShift.startsAt,
                  endTime: this.replacementShift.endsAt,
                  unpaidWorkingPeriods: this.replacementShift.scheduledBreaks
                    .filter((b) => !b.paid)
                    .map((b) => ({
                      startTime: b.startsAt || this.replacementShift.startsAt,
                      endTime: addMinutes(
                        b.startsAt || this.replacementShift.startsAt,
                        b.durationInMinutes,
                        this.timezone,
                      ),
                    })),
                },
                {
                  employeeId: this.recipient.id,
                  jobRoleId: this.unwantedShift.jobRoleId,
                  startTime: this.unwantedShift.startsAt,
                  endTime: this.unwantedShift.endsAt,
                  unpaidWorkingPeriods: this.unwantedShift.scheduledBreaks
                    .filter((b) => !b.paid)
                    .map((b) => ({
                      startTime: b.startsAt || this.unwantedShift.startsAt,
                      endTime: addMinutes(
                        b.startsAt || this.unwantedShift.startsAt,
                        b.durationInMinutes,
                        this.timezone,
                      ),
                    })),
                },
              ],
            },
          })
          .then(({ data }) => {
            this.shiftCosts = data.workingTimeCosts;
          });
      },
      deep: true,
      immediate: true,
    },
  },

  async created() {
    await this.swapShifts.promise;
    this.fetchOtherScheduledShifts();
  },

  methods: {
    isZeroHourEmployee,
    employeeName(employeeId: number): string {
      const employee = this.employees.data.find((e) => e.id === employeeId);
      return employee ? fullName(employee) : '';
    },

    fetchOtherScheduledShifts() {
      this.fetchAllEmployeesShiftsInSameWeek(this.unwantedShift).then(
        (shifts) => {
          this.requesterShifts = shifts;
        },
      );
      this.fetchAllEmployeesShiftsInSameWeek(this.replacementShift).then(
        (shifts) => {
          this.recipientShifts = shifts;
        },
      );
    },

    async fetchAllEmployeesShiftsInSameWeek(shift: Shift) {
      const weekStart = startOfWeek(
        shift.startsAt,
        this.startOfWeek,
        this.timezone,
      );
      const weekEnd = startOfNextWeek(
        shift.startsAt,
        this.startOfWeek,
        this.timezone,
      );
      const { data } = await shiftApi.listShifts({
        where: {
          employeeId: { eq: shift.employeeId },
          startsAt: {
            lt: weekEnd,
            gte: weekStart,
          },
        },
      });

      return data;
    },

    getShiftCost(employee: Employee): number {
      return (
        this.shiftCosts.find((c) => c.employeeId === employee.id)?.cost || 0
      );
    },

    async reject() {
      this.submitting = true;
      await shiftSwapApi
        .rejectShiftSwap({
          id: this.shiftSwap.id,
        })
        .then(() => {
          showSuccess(this.$t('successMessage.shiftSwapRejected'));
          this.open = false;
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    async approve() {
      this.submitting = true;
      await shiftSwapApi
        .approveShiftSwap({
          id: this.shiftSwap.id,
        })
        .then(() => {
          showSuccess(this.$t('successMessage.shiftSwapApproved'));
          this.open = false;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
